/* ============================================================
   NORTHSHEEP — two-handle price slider for the price filter.

   Markup: snippets/price-facet.liquid. Styles: assets/ns-price-slider.css.
   Off switch: Theme settings → Northsheep filters → "Price filter as a slider".

   HOW IT WORKS
   Dawn's two price text boxes are still in the form, only hidden. Dragging a
   handle writes the whole-number price into those boxes (empty at either end,
   so "all prices" sends no price filter at all). Nothing here submits
   anything: the range inputs sit inside Dawn's filter form, their `input`
   events bubble to it, and FacetFiltersForm's own 800 ms debounce sends the
   form once the handle comes to rest — exactly as it did for typing.

   The range inputs have no name, so they never reach the URL. They are placed
   AFTER the text boxes because Dawn's PriceRange reads inputs[0] / inputs[1]
   as "from" / "to".

   Dawn re-renders the filter markup after every change; a custom element
   upgrades itself on the new markup, so there is nothing to re-bind.
   ============================================================ */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('ns-price-slider')) return;

  class NsPriceSlider extends HTMLElement {
    connectedCallback() {
      if (this.__nsReady) return;
      this.__nsReady = true;

      this.lo = this.querySelector('[data-ns-ps-lo]');
      this.hi = this.querySelector('[data-ns-ps-hi]');
      this.fill = this.querySelector('[data-ns-ps-fill]');
      this.loLabel = this.querySelector('[data-ns-ps-lo-label]');
      this.hiLabel = this.querySelector('[data-ns-ps-hi-label]');
      this.max = Number(this.getAttribute('data-max')) || 0;

      var scope = this.closest('price-range') || this.parentElement;
      this.gte = scope ? scope.querySelector('input[name$=".gte"]') : null;
      this.lte = scope ? scope.querySelector('input[name$=".lte"]') : null;

      if (!this.lo || !this.hi || !this.max) return;

      this.min = 0;
      var self = this;
      this.lo.addEventListener('input', function () { self.sync(self.lo); });
      this.hi.addEventListener('input', function () { self.sync(self.hi); });
      this.paint();
      this.primeFloor();
    }

    /* THE LOW END (owner, 2026-09-15): the bar runs from the collection's
       lowest price to its highest, handles resting at both ends. Resting at
       an end sends no price filter, so no "Price" chip appears until a handle
       is actually moved. Shopify only reports the highest price, so the
       lowest comes from templates/collection.ns-price-floor.liquid — fetched
       when the slider is about to be seen (its drawer panel opens), and
       remembered for the rest of the visit. */
    primeFloor() {
      var url = this.getAttribute('data-floor-url');
      if (!url) return;
      var key = 'northsheep_price_floor:' + url;
      var cached = null;
      try { cached = sessionStorage.getItem(key); } catch (e) {}
      if (cached !== null) { this.applyFloor(Number(cached)); return; }

      var self = this, started = false;
      function go() {
        if (started) return;
        started = true;
        fetch(url, { headers: { Accept: 'text/html' } })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (t) {
            var cents = parseInt(String(t).trim(), 10);
            if (!(cents >= 0)) return;
            try { sessionStorage.setItem(key, String(cents)); } catch (e) {}
            self.applyFloor(cents);
          })
          .catch(function () { /* keep €0 as the low end */ });
      }
      if (window.IntersectionObserver) {
        var io = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) { io.disconnect(); go(); }
        });
        io.observe(this);
      } else {
        go();
      }
      // A tap on any filter opener is the earliest warning on a phone.
      document.addEventListener('pointerdown', function (e) {
        var t = e.target;
        if (t && t.closest && t.closest('.mobile-facets__open, .mobile-facets__summary, .facets__summary')) go();
      }, { passive: true });
    }

    applyFloor(cents) {
      var floor = Math.floor(cents / 100);
      if (!(floor > 0) || floor >= this.max) return;
      this.min = floor;
      [this.lo, this.hi].forEach(function (r) {
        r.min = String(floor);
        r.setAttribute('data-min', String(floor));
      });
      // No price filter yet: rest the low handle on the new low end.
      if (!this.gte || this.gte.value === '' || Number(this.lo.value) < floor) this.lo.value = String(floor);
      if (Number(this.hi.value) < floor) this.hi.value = String(floor);
      this.paint();
    }

    /* Runs before the event bubbles to Dawn's form, so the debounced submit
       always reads the values set here. */
    sync(moved) {
      var lo = Number(this.lo.value);
      var hi = Number(this.hi.value);
      if (lo > hi) {
        if (moved === this.lo) { lo = hi; this.lo.value = String(lo); }
        else { hi = lo; this.hi.value = String(hi); }
      }
      if (this.gte) this.gte.value = lo > this.min ? String(lo) : '';
      if (this.lte) this.lte.value = hi < this.max ? String(hi) : '';
      this.paint();
    }

    /* data-format is the store's own money format rendered for 12345
       ("€12.345", "12.345 €", "£12,345", "CHF 12'345"), so the label follows
       each store's currency and symbol position with no locale lookup. */
    format(n) {
      var f = this.getAttribute('data-format') || '';
      return /\d/.test(f) ? f.replace(/\d(?:[\d.,'\s ]*\d)?/, String(n)) : String(n);
    }

    paint() {
      var lo = Number(this.lo.value);
      var hi = Number(this.hi.value);
      var max = this.max;
      var min = this.min || 0;
      var span = Math.max(1, max - min);
      if (this.fill) {
        this.fill.style.left = ((lo - min) / span) * 100 + '%';
        this.fill.style.right = 100 - ((hi - min) / span) * 100 + '%';
      }
      if (this.loLabel) this.loLabel.textContent = this.format(lo);
      if (this.hiLabel) this.hiLabel.textContent = this.format(hi);
      this.lo.setAttribute('aria-valuetext', this.format(lo));
      this.hi.setAttribute('aria-valuetext', this.format(hi));
      /* With both handles at the top end the upper handle covers the lower
         one, and the range could never be dragged back down. Lift the lower
         handle in exactly that case. */
      this.lo.style.zIndex = lo >= max ? '4' : '';
    }
  }

  customElements.define('ns-price-slider', NsPriceSlider);
})();
